/** Hooks TanStack Query pour la gestion des plannings */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL;

function getHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/** Relance l'erreur de l'API avec son message métier (champ `detail`). */
async function throwApiError(res: Response): Promise<never> {
  const detail = await res
    .json()
    .then((body) => body?.detail)
    .catch(() => null);
  throw new Error(detail || `Erreur ${res.status}`);
}

/** Popup d'erreur commun aux mutations : sans lui, un refus de l'API est muet. */
function alertError(err: unknown) {
  window.alert(err instanceof Error ? err.message : 'Erreur');
}

// Types
export interface SlotData {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_working: boolean;
  // Spécificités métier portées par créneau
  zone_ids: number[];
  epi_ids: number[];
  round_id: number | null;
  vehicle_id: number | null;
}

// Types de sortie (référentiels embarqués par le backend dans chaque créneau)
export interface RefItem {
  id: number;
  name: string;
}
export interface VehicleRef {
  id: number;
  name: string;
  license_plate: string | null;
}
export interface SlotResponse {
  day_of_week: number;
  // null = jour travaillé sans horaires connus (tournée sans horaires standards)
  start_time: string | null;
  end_time: string | null;
  is_working: boolean;
  zones: RefItem[];
  epis: RefItem[];
  round: RefItem | null;
  vehicle: VehicleRef | null;
}

export interface WorkSchedule {
  id: number;
  name: string;
  activity: 'boulangerie' | 'traiteur' | 'chambre_froide' | 'cleaning' | 'driving' | 'office';
  is_active: boolean;
  slots: SlotResponse[];
}

export interface ScheduleAssignment {
  id: number;
  employee_id: number;
  employee_name: string;
  schedule_id: number;
  schedule_name: string;
  effective_from: string;
  effective_to: string | null;
}

// API
async function fetchSchedules(activity?: string): Promise<WorkSchedule[]> {
  const params = activity ? `?activity=${activity}` : '';
  const res = await fetch(`${API_URL}/api/schedules${params}`, { headers: getHeaders() });
  return res.json();
}

async function fetchAssignments(site_id?: number): Promise<ScheduleAssignment[]> {
  const params = site_id ? `?site_id=${site_id}` : '';
  const res = await fetch(`${API_URL}/api/schedules/assignments${params}`, { headers: getHeaders() });
  return res.json();
}

// Hooks
export function useSchedules(activity?: string) {
  return useQuery({
    queryKey: ['schedules', activity],
    queryFn: () => fetchSchedules(activity),
  });
}

export function useScheduleAssignments(site_id?: number) {
  return useQuery({
    queryKey: ['schedule-assignments', site_id],
    queryFn: () => fetchAssignments(site_id),
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; activity: string; slots: SlotData[] }) => {
      const res = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) await throwApiError(res);
      return res.json();
    },
    onError: alertError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; name: string; slots: SlotData[] }) => {
      const { id, ...body } = data;
      const res = await fetch(`${API_URL}/api/schedules/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) await throwApiError(res);
      return res.json();
    },
    onError: alertError,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['schedule-weekly'] });
    },
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_URL}/api/schedules/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!res.ok) await throwApiError(res);
    },
    onError: alertError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

// --- Vue hebdo ---

export type AbsenceReason = 'conge' | 'maladie' | 'autre' | 'repos' | 'astreinte';

export interface AbsentDay {
  day: number;
  reason: AbsenceReason;
}

/** Chauffeurs : tournée affectée à un jour (une par jour). */
export interface DayRound {
  day: number;
  round_id: number;
  round_name: string;
}

export interface WeeklyEmployeeEntry {
  employee_id: number;
  employee_name: string;
  site_name: string | null;
  schedule_id: number | null;
  schedule_name: string | null;
  is_override: boolean;
  working_days: number[];
  absent_days: number[];
  absent_details: AbsentDay[];
  day_rounds: DayRound[];
}

export interface WeeklyViewResponse {
  week_start: string;
  entries: WeeklyEmployeeEntry[];
}

export function useWeeklyView(week_start: string, site_id?: number) {
  return useQuery({
    queryKey: ['schedule-weekly', week_start, site_id],
    queryFn: async (): Promise<WeeklyViewResponse> => {
      const params = new URLSearchParams({ week_start });
      if (site_id) params.set('site_id', String(site_id));
      const res = await fetch(`${API_URL}/api/schedules/weekly?${params}`, {
        headers: getHeaders(),
      });
      return res.json();
    },
    enabled: !!week_start,
  });
}

// --- Comparaison pointages vs planning ---

export interface PlannedSegment {
  start: string;
  end: string;
  hours: number;
}

export interface DailyComparison {
  date: string;
  planned_hours: number;
  actual_hours: number;
  is_absent: boolean;
  planned_start: string | null;
  planned_end: string | null;
  planned_segments: PlannedSegment[];
  actual_in: string | null;
  actual_out: string | null;
}

export interface ComparisonEntry {
  employee_id: number;
  employee_name: string;
  site_name: string | null;
  activity: string;
  schedule_name: string | null;
  days: DailyComparison[];
  week_planned: number;
  week_actual: number;
  month_planned: number;
  month_actual: number;
}

export interface WeeklyComparisonResponse {
  week_start: string;
  month_start: string;
  entries: ComparisonEntry[];
}

export interface MonthlyEmployeeEntry {
  employee_id: number;
  employee_name: string;
  site_name: string | null;
  activity: string;
  schedule_name: string | null;
  days: DailyComparison[];
  month_planned: number;
  month_actual: number;
}

export interface MonthlyComparisonResponse {
  month_start: string;
  month_end: string;
  entries: MonthlyEmployeeEntry[];
}

export type Activity = 'boulangerie' | 'traiteur' | 'chambre_froide' | 'cleaning' | 'driving' | 'office';

export function useMonthlyComparison(month: string, site_id?: number, activity?: Activity) {
  return useQuery({
    queryKey: ['schedule-comparison-month', month, site_id, activity],
    queryFn: async (): Promise<MonthlyComparisonResponse> => {
      const params = new URLSearchParams({ month });
      if (site_id) params.set('site_id', String(site_id));
      if (activity) params.set('activity', activity);
      const res = await fetch(`${API_URL}/api/schedules/monthly-comparison?${params}`, {
        headers: getHeaders(),
      });
      return res.json();
    },
    enabled: !!month,
  });
}

export function useWeeklyComparison(week_start: string, site_id?: number, activity?: Activity) {
  return useQuery({
    queryKey: ['schedule-comparison', week_start, site_id, activity],
    queryFn: async (): Promise<WeeklyComparisonResponse> => {
      const params = new URLSearchParams({ week_start });
      if (site_id) params.set('site_id', String(site_id));
      if (activity) params.set('activity', activity);
      const res = await fetch(`${API_URL}/api/schedules/weekly-comparison?${params}`, {
        headers: getHeaders(),
      });
      return res.json();
    },
    enabled: !!week_start,
  });
}

export function useSetWeeklyOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      employee_id: number;
      week_start: string;
      schedule_id: number | null;
    }) => {
      const res = await fetch(`${API_URL}/api/schedules/weekly/override`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) await throwApiError(res);
    },
    onError: alertError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-weekly'] }),
  });
}

export function useSetDriverDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      employee_id: number;
      week_start: string;
      day_of_week: number;
      round_id: number | null;
    }) => {
      const res = await fetch(`${API_URL}/api/schedules/weekly/driver-day`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) await throwApiError(res);
    },
    onError: alertError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-weekly'] }),
  });
}

export function useSetWeeklyAbsentDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      employee_id: number;
      week_start: string;
      absent_days: AbsentDay[];
    }) => {
      const res = await fetch(`${API_URL}/api/schedules/weekly/absent-days`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) await throwApiError(res);
    },
    onError: alertError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-weekly'] }),
  });
}

export function useAssignSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { employee_id: number; schedule_id: number; effective_from: string }) => {
      const res = await fetch(`${API_URL}/api/schedules/assignments`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) await throwApiError(res);
      return res.json();
    },
    onError: alertError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-assignments'] }),
  });
}
